import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getErrorMessage } from "@/lib/utils";
import { motion } from "framer-motion";
import { Wrench, MapPin, ArrowRight, CheckCircle2, Smartphone, Info, CreditCard, Loader2, Battery, CircleDot, Fuel, Key, Truck, LocateFixed, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SavedCardsSelector from "@/components/SavedCardsSelector";
import MapLocationPicker from "@/components/MapLocationPicker";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";



const RoadsideAssistancePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState("new");
  const [locationLoading, setLocationLoading] = useState(false);
  const [pickupCoords, setPickupCoords] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_phone: user.phone || prev.customer_phone,
        customer_email: user.email || prev.customer_email,
      }));
    }
  }, [user]);

  const [formData, setFormData] = useState({
    location: "",
    location_coordinates: null,
    service_type: "",
    vehicle_make_model: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    notes: ""
  });

  const serviceTypes = [
    { value: "battery_boost", label: "Battery Boost", desc: "Get a jump-start for a dead battery", icon: Battery, price: 50 },
    { value: "flat_tire", label: "Flat Tire Change", desc: "Have your spare tire installed", icon: CircleDot, price: 60 },
    { value: "fuel_delivery", label: "Fuel Delivery", desc: "Receive fuel if your tank is empty", icon: Fuel, price: 55 },
    { value: "lockout", label: "Lockout Service", desc: "Get help unlocking your vehicle", icon: Key, price: 65 },
    { value: "winching", label: "Winching / Extrication", desc: "Pull your vehicle out of snow, mud, or a ditch", icon: Truck, price: 100 }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const useMyLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setLocationLoading(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const coords = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
          );
          const data = await response.json();
          
          if (data.display_name) {
            setFormData(prev => ({
              ...prev,
              location: data.display_name,
              location_coordinates: coords
            }));
            toast.success("Location detected successfully!");
          } else {
            setFormData(prev => ({
              ...prev,
              location: `GPS: ${coords}`,
              location_coordinates: coords
            }));
            toast.success("GPS coordinates captured!");
          }
        } catch (error) {
          setFormData(prev => ({
            ...prev,
            location: `GPS: ${coords}`,
            location_coordinates: coords
          }));
          toast.success("GPS coordinates captured!");
        }
        
        setLocationLoading(false);
      },
      (error) => {
        setLocationLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error("Please allow location access to use this feature");
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Location information is unavailable");
            break;
          case error.TIMEOUT:
            toast.error("Location request timed out");
            break;
          default:
            toast.error("Unable to get your location");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const getSelectedService = () => {
    return serviceTypes.find(s => s.value === formData.service_type);
  };

  const proceedToDetails = async () => {
    if (!formData.location || !formData.service_type) {
      toast.error("Please select your location and service type");
      return;
    }
    if (!pickupCoords) {
      toast.error("Please select a valid location on the map");
      return;
    }
    if (!formData.vehicle_make_model) {
      toast.error("Please enter your vehicle make & model");
      return;
    }
    const service = getSelectedService();
    setEstimate({ max_price: service.price, min_price: service.price });
    setStep(2);
  };

  const proceedToPayment = async () => {
    setPaymentLoading(true);
    try {
      const service = getSelectedService();
      const amount = estimate.max_price || service?.price || 0;
      const bookingResponse = await axios.post(`${API}/roadside/booking`, {
        ...formData,
        service_price: amount,
      });
      const booking = bookingResponse.data;
      const serviceData = {
        booking_id: booking.id,
        booking_type: "roadside",
        amount,
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
        service_type: "roadside_assistance",
        pickup_address: formData.location,
        dropoff_address: "",
        pickup_coords: pickupCoords,
      };

      if (selectedCardId && selectedCardId !== "new") {
        const authResponse = await axios.post(`${API}/payments/authorize-hold`, {
          ...serviceData,
          payment_method_id: selectedCardId,
        }, { withCredentials: true });
        if (authResponse.data.success) {
          toast.success("Payment authorized! Finding your helper...");
          navigate("/matching", {
            state: {
              booking_id: booking.id,
              booking_type: "roadside",
              service_type: "roadside_assistance",
              roadside_sub_service: formData.service_type,
              pickup_lat: pickupCoords.latitude,
              pickup_lng: pickupCoords.longitude,
              pickup_address: formData.location,
            },
          });
        } else {
          toast.error("Payment authorization failed. Please try again.");
        }
        setPaymentLoading(false);
      } else {
        const paymentResponse = await axios.post(`${API}/payments/create-checkout`, {
          ...serviceData,
          origin_url: window.location.origin,
        });
        window.location.href = paymentResponse.data.checkout_url;
      }
    } catch (error) {
      console.error("Authorization error:", error);
      toast.error(getErrorMessage(error, "Payment authorization failed. Please try another payment method."));
      setPaymentLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="roadside-assistance-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-900 via-red-800 to-orange-900"></div>
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="section-container relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-lg">
              <Wrench className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="heading-2 text-white mb-4">Roadside Assistance</h1>
            <p className="body-lg text-red-100">
              Stranded on the road? Request quick roadside help from nearby drivers in minutes. Select the service you need and a helper will come directly to your location.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Flow */}
      <section className="py-16">
        <div className="section-container">
          {/* Select Service */}
          {step === 1 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="max-w-2xl mx-auto">
              <div className="bg-white rounded-3xl p-8 shadow-lg border border-stone-100">
                <h2 className="text-2xl font-bold text-emerald-950 mb-6">What help do you need?</h2>
                
                <div className="space-y-6">
                  {/* Location with Map */}
                  <MapLocationPicker
                    pickupAddress={formData.location}
                    onPickupChange={(val) => handleInputChange("location", val)}
                    onPickupCoordsChange={(c) => { setPickupCoords(c); handleInputChange("location_coordinates", `${c.latitude}, ${c.longitude}`); }}
                    singleLocation={true}
                    locationLabel="Your Location *"
                    pickupPlaceholder="Where are you stranded?"
                    serviceType="roadside_assistance"
                  />

                  {/* Service Type */}
                  <div>
                    <Label className="text-emerald-950 font-medium mb-3 block">Select Service *</Label>
                    <div className="space-y-3">
                      {serviceTypes.map((service) => (
                        <button
                          key={service.value}
                          type="button"
                          onClick={() => handleInputChange("service_type", service.value)}
                          className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
                            formData.service_type === service.value
                              ? "border-red-500 bg-red-50"
                              : "border-stone-200 hover:border-red-300"
                          }`}
                          data-testid={`service-${service.value}`}
                        >
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            formData.service_type === service.value ? "bg-red-500 text-white" : "bg-stone-100 text-stone-600"
                          }`}>
                            <service.icon className="w-6 h-6" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-emerald-950">{service.label}</p>
                            <p className="text-sm text-stone-500">{service.desc}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-emerald-950 font-medium mb-2 block">Vehicle Make & Model *</Label>
                    <Input
                      className="input-style"
                      placeholder="e.g. Honda Civic 2021"
                      value={formData.vehicle_make_model}
                      onChange={(e) => handleInputChange("vehicle_make_model", e.target.value)}
                      data-testid="vehicle-make-model-input"
                    />
                  </div>

                  <Button onClick={proceedToDetails} disabled={!formData.location || !formData.service_type} className="w-full btn-primary bg-red-500 hover:bg-red-600" data-testid="continue-btn">
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
            )}

            {step === 2 && estimate && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8 mt-6" data-testid="payment-step">
                <h2 className="text-xl font-bold text-emerald-950 mb-4">Payment</h2>
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-stone-600">Service Price</p>
                  <p className="text-3xl font-bold text-red-600">${estimate.max_price.toFixed(2)} <span className="text-sm font-normal text-stone-500">CAD</span></p>
                </div>
                <SavedCardsSelector onSelect={setSelectedCardId} selectedCardId={selectedCardId} />
                <Button className="w-full bg-red-500 hover:bg-red-600 text-white rounded-full py-6 font-bold mt-4" onClick={proceedToPayment} disabled={paymentLoading} data-testid="pay-btn">
                  {paymentLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <>Pay & Find Help <ArrowRight className="w-5 h-5 ml-2" /></>}
                </Button>
              </motion.div>
            )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RoadsideAssistancePage;
